import axios from 'axios';
import { BUILD_ID } from '../buildInfo';
import { getApiBase } from './serverConfig';
import { IS_NATIVE, API_CONTRACT_VERSION } from './platform';
import { forgetUnownedDraft } from './unownedDraft';
import { answerOutdated } from './versionSkew';
import { announceReadOnly, forgetAdmin, restoreAdmin, viewingAs } from './viewAs';

const api = axios.create();

// Resolved per request, not once at module load. A store binary cannot be
// rebuilt to follow a server move, and a field laptop's LAN address is not
// knowable at build time -- so the operator can repoint this client and the
// very next request honours it, with no reload. getApiBase() falls back to the
// compiled-in REACT_APP_API_URL, so every existing deployment is unaffected.
api.interceptors.request.use((config) => {
  config.baseURL = getApiBase();
  const token = localStorage.getItem('mb_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-App-Version'] = BUILD_ID;
  if (IS_NATIVE) {
    // A frozen store binary cannot match the backend's deploy SHA, so it
    // declares the API contract it was built against instead and the backend
    // accepts anything at or above its supported floor. Without this the first
    // backend deploy after a release 426s every write from every installed
    // copy -- and the handler below would answer by reloading the same frozen
    // bundle, forever.
    config.headers['X-Pitriot-Client'] = 'native';
    config.headers['X-Api-Version'] = String(API_CONTRACT_VERSION);
  }
  return config;
});

// A 401 from these means "those credentials are wrong", not "your session has
// ended", and the page that sent them says so. Handled as an ended session,
// a mistyped password on /login or /survey reloaded the page before the
// reason could be shown, and a mistyped current password on /change-password
// signed the person out. A refused Google credential (/auth/google) is the
// same kind of answer.
const CREDENTIAL_CHECK = /\/auth\/(login|change-password|google)(\?|$)/;

// A request sent with `keepSession: true` in its config is answered as it
// comes: Field Mode's outbox sends in the background, and a 401 there must
// not throw a collector out of the camera mid-specimen -- the outbox stops
// and waits for the next sign-in instead (utils/fieldOutbox.js).
export const endsSession = (error) =>
  error.response?.status === 401 && !CREDENTIAL_CHECK.test(error.config?.url || '')
  && !error.config?.keepSession;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (endsSession(error)) {
      // A view that ended (Exit in another tab, the hour, a sign-out
      // elsewhere) hands back the admin's own token, while it is good.
      if (viewingAs() && restoreAdmin()) {
        window.location.assign('/');
        return Promise.reject(error);
      }
      localStorage.removeItem('mb_token');
      forgetAdmin();
      forgetUnownedDraft();
      window.location.href = window.location.pathname.startsWith('/survey') ? '/survey' : '/login';
    }
    // Refused because this session may only read -- a view as another role,
    // or an executive's account (read_only_account): say so wherever the page
    // is, unless the request asked to be left quiet (`quietReadOnly`).
    if (error.response?.status === 403
        && ['view_as_read_only', 'read_only_account'].includes(error.response?.data?.code)
        && !error.config?.quietReadOnly) {
      announceReadOnly(error.response.data.role);
    }
    if (error.response?.status === 426) {
      if (IS_NATIVE) {
        // Reloading would re-run the same bundle and 426 again. Say what has
        // actually happened and leave the app usable for reading.
        window.alert(error.response?.data?.error ||
          'This version of the app is no longer supported. Please update it.');
      } else {
        // Reload only when a newer web build is actually published, and then
        // at most once per build pair and twice per half hour; otherwise this
        // page is the newest and the server is mid-deploy, so the error says
        // so instead (utils/versionSkew.js). An unconditional reload here
        // looped for as long as the Lambda lagged behind Amplify.
        try {
          await answerOutdated(error);
        } catch {
          // Whatever happened, the caller still gets the refusal below.
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;